import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ordersAPI } from '../../utils/api';
import { useAuthStore } from '../../store';

const STATUS_COLOR = {
  pending:   { bg: '#fef9c3', color: '#854d0e' },
  placed:    { bg: '#dbeafe', color: '#1e40af' },
  packed:    { bg: '#e0e7ff', color: '#3730a3' },
  shipped:   { bg: '#d1fae5', color: '#065f46' },
  delivered: { bg: '#bbf7d0', color: '#14532d' },
  cancelled: { bg: '#fee2e2', color: '#991b1b' },
};

const PAYMENT_COLOR = {
  paid:    { bg: '#dcfce7', color: '#15803d' },
  pending: { bg: '#fef9c3', color: '#854d0e' },
  failed:  { bg: '#fee2e2', color: '#991b1b' },
};

const Badge = ({ label, colors }) => (
  <span style={{
    background: colors?.bg || '#f3f4f6',
    color: colors?.color || '#374151',
    padding: '0.2rem 0.6rem',
    borderRadius: 20,
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  }}>
    {label}
  </span>
);

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }

    ordersAPI.getAll({ limit: 50 })
      .then(r => {
        // Backend returns { orders, total, page, pages }
        const list = r.data?.orders || r.data || [];
        setOrders(list);
      })
      .catch(() => setError('Could not load orders. Please try again.'))
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) return (
    <div className="container" style={{ padding: '3rem 1rem', textAlign: 'center', color: '#6b7280' }}>
      Loading your orders...
    </div>
  );

  return (
    <div className="container" style={{ padding: '1.5rem 1rem', maxWidth: 860 }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1.5rem' }}>My Orders</h1>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      {orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
          <h2 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>No orders yet</h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            You haven't placed any orders. Start shopping!
          </p>
          <Link to="/kits" className="btn btn-primary">Browse School Kits</Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {orders.map(order => (
            <div
              key={order.id}
              className="card"
              style={{ padding: '1rem 1.25rem', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
              onClick={() => navigate(`/orders/${order.order_number}`)}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                {/* Left */}
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.25rem' }}>
                    Order #{order.order_number}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                    {new Date(order.created_at).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'short', day: 'numeric',
                    })}
                  </div>
                </div>

                {/* Right — badges */}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge
                    label={order.status}
                    colors={STATUS_COLOR[order.status]}
                  />
                  <Badge
                    label={order.payment_status}
                    colors={PAYMENT_COLOR[order.payment_status]}
                  />
                </div>
              </div>

              {/* Items preview */}
              {order.items?.length > 0 && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.82rem', color: '#4b5563' }}>
                  {order.items.slice(0, 2).map(item => (
                    <span key={item.id} style={{ marginRight: '0.75rem' }}>
                      {item.name} × {item.qty}
                    </span>
                  ))}
                  {order.items.length > 2 && (
                    <span style={{ color: '#9ca3af' }}>+{order.items.length - 2} more</span>
                  )}
                </div>
              )}

              {/* Footer */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginTop: '0.75rem', paddingTop: '0.75rem',
                borderTop: '1px solid #f3f4f6',
              }}>
                <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                  Payment: <strong>{order.payment_method || 'N/A'}</strong>
                </div>
                <div style={{ fontWeight: 800, fontSize: '1rem' }}>
                  ${parseFloat(order.total).toFixed(2)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyOrdersPage;
